import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { pluginRoutes } from "@/composition";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { supervisedProfileRoute } from "@/routes/_app/supervised.$profileId";
import { supervisedTransparencyRoute } from "@/routes/_app/supervised.$profileId.transparency";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profilePlanRoute } from "@/routes/_app/profile.plan";
import { cc21ProbeRoute } from "@/routes/_app/cc21-probe";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    // CC-21 regression fixture (Select/Popover reachable inside a modal
    // Dialog). DEV only: it serves the cc21-probe.spec e2e and is NOT registered in prod.
    ...(import.meta.env.DEV ? [cc21ProbeRoute] : []),
    indexRoute, // path: "/" — Panel del tutor (lista de perfiles supervisados)
    ...pluginRoutes,
    supervisedProfileRoute,
    supervisedTransparencyRoute,
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profilePlanRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). Eager viewport preloading saturated the main thread after login
  // in the template — keep the calm hover-only behaviour.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
  // Any unknown URL shows the premium "not found" state (with
  // providers available) instead of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
